import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import { Button } from "../Button";
import { Loader } from "../Loader";
import { ROUTES } from "../../config/routes";
import { ReactComponent as CloseIcon } from "../../assets/icons/x.svg";
import { BusinessAccountTransactionViewModel } from "../../api/generated/models/BusinessAccountTransactionViewModel";
import { VisaAdminService } from "../../api/generated/services/VisaAdminService";
import { GetTransactionHistoryByIdResult } from "../../api/generated/models/GetTransactionHistoryByIdResult";
import { TransactionHistorySuccessDto } from "../../api/generated/models/TransactionHistorySuccessDto";
import { TransactionHistoryFailureDto } from "../../api/generated/models/TransactionHistoryFailureDto";
import "../../api/openApiConfig";

interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: BusinessAccountTransactionViewModel | null;
}

const yesNo = (value: boolean | undefined): string =>
  value === undefined ? "-" : value ? "Yes" : "No";

const orDash = (value: string | number | null | undefined): string =>
  value === null || value === undefined || value === "" ? "-" : String(value);

const formatCopenhagen = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  try {
    return new Date(dateString).toLocaleString("da-DK", {
      timeZone: "Europe/Copenhagen",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return dateString;
  }
};

const formatAmount = (
  amount: number | undefined,
  currency: string | undefined
): string => {
  if (amount === undefined) return "-";
  return `${amount.toFixed(2)} ${currency || ""}`.trim();
};

const formatJson = (jsonString: string | null | undefined): string => {
  if (!jsonString) return "No data";
  try {
    return JSON.stringify(JSON.parse(jsonString), null, 2);
  } catch {
    return jsonString;
  }
};

interface ParsedJsonData {
  identityId?: string;
  contentfulBusinessId?: string;
  transactionTimestamp?: string;
  cardNumberLastFour?: string;
  transactionAmount?: number;
  transactionCurrency?: string;
}

const parseJsonSentToBackend = (
  json: string | null | undefined
): ParsedJsonData => {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json);
    return {
      identityId: parsed.identityId || undefined,
      contentfulBusinessId: parsed.contentfulBusinessId || undefined,
      transactionTimestamp: parsed.transactionTimestamp || undefined,
      cardNumberLastFour: parsed.cardNumberLastFour || undefined,
      transactionAmount: parsed.transactionAmount || undefined,
      transactionCurrency: parsed.transactionCurrency || undefined,
    };
  } catch {
    return {};
  }
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div className="text-sm text-gray-500">{label}</div>
    <div className="break-all">{children}</div>
  </div>
);

// Renders the raw Visa transaction history (successes, failures, and each
// attempt's raw Visa event JSON) fetched on mount. Mounted only when the
// Visa details section is shown, so opening the modal triggers the fetch.
const VisaRawHistorySection: React.FC<{ uniqueTransactionId: string }> = ({
  uniqueTransactionId,
}) => {
  const [history, setHistory] = useState<GetTransactionHistoryByIdResult | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const result =
          await VisaAdminService.postApiVisaAdminGetTransactionHistoryById({
            uniqueTransactionId,
          });
        if (ignore) return;
        setHistory(result);
      } catch (err) {
        if (ignore) return;
        setError("Failed to load raw Visa history");
        console.error("Error fetching raw Visa history:", err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
  }, [uniqueTransactionId]);

  const toggle = (rowKey: string) =>
    setExpanded((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));

  const renderEventRow = (
    rowKey: string,
    visaEventId: string | null | undefined,
    colSpan: number
  ) => {
    if (!expanded[rowKey]) return null;
    const event =
      visaEventId && history?.visaEvents ? history.visaEvents[visaEventId] : null;
    return (
      <tr>
        <td colSpan={colSpan} className="bg-gray-50">
          {event ? (
            <pre className="bg-gray-100 p-3 rounded-lg overflow-x-auto text-xs font-mono max-h-[300px] overflow-y-auto">
              {formatJson(event.eventData)}
            </pre>
          ) : (
            <div className="text-gray-500 text-sm py-2">
              No Visa event data for this attempt.
            </div>
          )}
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
        {error}
      </div>
    );
  }

  if (!history) {
    return (
      <div className="text-gray-500 text-sm">No raw Visa history found.</div>
    );
  }

  const successes = history.successes ?? [];
  const failures = history.failures ?? [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Status">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              history.status === "Succeeded"
                ? "bg-green-100 text-green-800"
                : history.status === "Failed"
                ? "bg-red-100 text-red-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {orDash(history.status)}
          </span>
        </Field>
        <Field label="Last Updated At">
          {formatCopenhagen(history.lastUpdatedAt)}
        </Field>
      </div>

      {/* Successes */}
      <div>
        <div className="text-sm font-medium text-gray-900 mb-2">
          Successes ({successes.length})
        </div>
        {successes.length === 0 ? (
          <div className="text-gray-500 text-sm">No successful attempts</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Sent At</th>
                  <th>Identity ID</th>
                  <th>Business ID</th>
                  <th>Tx Timestamp</th>
                  <th>Card Last 4</th>
                  <th>Amount</th>
                  <th>
                    <span className="sr-only">Event</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {successes.map(
                  (success: TransactionHistorySuccessDto, index: number) => {
                    const jsonData = parseJsonSentToBackend(
                      success.jsonSentToPrimaryBackend
                    );
                    const rowKey = `success-${index}`;
                    return (
                      <React.Fragment key={rowKey}>
                        <tr>
                          <td className="whitespace-nowrap">
                            {formatCopenhagen(success.succeededAt)}
                          </td>
                          <td className="font-mono text-xs whitespace-nowrap">
                            {orDash(jsonData.identityId)}
                          </td>
                          <td className="font-mono text-xs whitespace-nowrap">
                            {orDash(jsonData.contentfulBusinessId)}
                          </td>
                          <td className="whitespace-nowrap">
                            {formatCopenhagen(jsonData.transactionTimestamp)}
                          </td>
                          <td className="font-mono whitespace-nowrap">
                            {orDash(jsonData.cardNumberLastFour)}
                          </td>
                          <td className="whitespace-nowrap">
                            {formatAmount(
                              jsonData.transactionAmount,
                              jsonData.transactionCurrency
                            )}
                          </td>
                          <td className="whitespace-nowrap">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!success.visaEventId}
                              onClick={() => toggle(rowKey)}
                            >
                              {expanded[rowKey] ? "Hide event" : "Event"}
                            </Button>
                          </td>
                        </tr>
                        {renderEventRow(rowKey, success.visaEventId, 7)}
                      </React.Fragment>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Failures */}
      <div>
        <div className="text-sm font-medium text-gray-900 mb-2">
          Failures ({failures.length})
        </div>
        {failures.length === 0 ? (
          <div className="text-gray-500 text-sm">No failed attempts</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Failed At</th>
                  <th>Error Code</th>
                  <th>Error Message</th>
                  <th>Identity ID</th>
                  <th>Business ID</th>
                  <th>Tx Timestamp</th>
                  <th>Card Last 4</th>
                  <th>Amount</th>
                  <th>
                    <span className="sr-only">Event</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {failures.map(
                  (failure: TransactionHistoryFailureDto, index: number) => {
                    const jsonData = parseJsonSentToBackend(
                      failure.jsonSentToPrimaryBackend
                    );
                    const rowKey = `failure-${index}`;
                    return (
                      <React.Fragment key={rowKey}>
                        <tr>
                          <td className="whitespace-nowrap">
                            {formatCopenhagen(failure.failedAt)}
                          </td>
                          <td className="whitespace-nowrap">
                            {orDash(failure.errorCode)}
                          </td>
                          <td className="whitespace-nowrap">
                            {orDash(failure.errorMessage)}
                          </td>
                          <td className="font-mono text-xs whitespace-nowrap">
                            {orDash(jsonData.identityId)}
                          </td>
                          <td className="font-mono text-xs whitespace-nowrap">
                            {orDash(jsonData.contentfulBusinessId)}
                          </td>
                          <td className="whitespace-nowrap">
                            {formatCopenhagen(jsonData.transactionTimestamp)}
                          </td>
                          <td className="font-mono whitespace-nowrap">
                            {orDash(jsonData.cardNumberLastFour)}
                          </td>
                          <td className="whitespace-nowrap">
                            {formatAmount(
                              jsonData.transactionAmount,
                              jsonData.transactionCurrency
                            )}
                          </td>
                          <td className="whitespace-nowrap">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!failure.visaEventId}
                              onClick={() => toggle(rowKey)}
                            >
                              {expanded[rowKey] ? "Hide event" : "Event"}
                            </Button>
                          </td>
                        </tr>
                        {renderEventRow(rowKey, failure.visaEventId, 9)}
                      </React.Fragment>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  isOpen,
  onClose,
  transaction,
}) => {
  const navigate = useNavigate();
  const identityId = transaction?.identityId;

  const goToUser = () => {
    if (!identityId) return;
    navigate(`${ROUTES.ADMIN_USERS()}?search=${encodeURIComponent(identityId)}`);
  };

  const renderBody = () => {
    if (!transaction) return null;

    if (transaction.visaDetails) {
      const v = transaction.visaDetails;
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Unique Transaction ID">
              <span className="font-mono text-sm">
                {orDash(v.uniqueTransactionId)}
              </span>
            </Field>
            <Field label="Card ID">
              <span className="font-mono text-sm">{orDash(v.cardId)}</span>
            </Field>
            <Field label="Card Last 4">{orDash(v.cardNumberLastFour)}</Field>
            <Field label="Currency">{orDash(v.transactionCurrency)}</Field>
            <Field label="Reported Transaction Time">
              {formatCopenhagen(v.reportedTransactionTime)}
            </Field>
            <Field label="First Transaction With Business">
              {yesNo(v.isFirstTransactionWithBusiness)}
            </Field>
          </div>
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Raw Visa History
            </h3>
            {v.uniqueTransactionId ? (
              <VisaRawHistorySection
                key={v.uniqueTransactionId}
                uniqueTransactionId={v.uniqueTransactionId}
              />
            ) : (
              <div className="text-gray-500 text-sm">
                No unique transaction id available.
              </div>
            )}
          </div>
        </div>
      );
    }

    if (transaction.storeboxDetails) {
      const s = transaction.storeboxDetails;
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Card Acceptor ID">{orDash(s.cardAcceptorId)}</Field>
          <Field label="Card Acceptor Info">{orDash(s.cardAcceptorInfo)}</Field>
          <Field label="Merchant CVR">{orDash(s.merchantCvr)}</Field>
          <Field label="Reference ID">
            <span className="font-mono text-sm">{orDash(s.referenceId)}</span>
          </Field>
          <Field label="User Info">{orDash(s.userInfo)}</Field>
          <Field label="Transaction Type">{orDash(s.transactionType)}</Field>
          <Field label="Processing Code">{orDash(s.processingCode)}</Field>
          <Field label="External Card ID">
            <span className="font-mono text-sm">{orDash(s.externalCardId)}</span>
          </Field>
          <Field label="Offline Transaction">{yesNo(s.isOfflineTransaction)}</Field>
          <Field label="Assignment State">{orDash(s.transactionAssignmentState)}</Field>
          <Field label="First Transaction With Business">
            {yesNo(s.isFirstTransactionWithBusiness)}
          </Field>
        </div>
      );
    }

    if (transaction.affiliateDetails) {
      const a = transaction.affiliateDetails;
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Network">{orDash(a.network)}</Field>
          <Field label="Status">{orDash(a.status)}</Field>
          <Field label="Order Value">{orDash(a.orderValue)}</Field>
          <Field label="Commission">{orDash(a.commission)}</Field>
          <Field label="Currency">{orDash(a.currency)}</Field>
          <Field label="Transaction Name">{orDash(a.transactionName)}</Field>
          <Field label="External Reference">
            <span className="font-mono text-sm">{orDash(a.externalReference)}</span>
          </Field>
          <Field label="Merchant ID">
            <span className="font-mono text-sm">{orDash(a.merchantId)}</span>
          </Field>
        </div>
      );
    }

    return (
      <div className="text-gray-500 text-sm">
        No additional details for this transaction source.
      </div>
    );
  };

  return (
    <Modal
      className="modal-content md:max-w-[900px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Transaction Details"
    >
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-primary font-medium text-xl">Transaction Details</h2>
        <Button onClick={onClose} variant="icon">
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      {transaction && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4 border-b border-gray-200">
            <Field label="Source">{orDash(transaction.source)}</Field>
            <Field label="Identity ID">
              <span className="font-mono text-sm">{orDash(transaction.identityId)}</span>
            </Field>
          </div>
          {renderBody()}
        </div>
      )}

      <div className="flex justify-between mt-6 pt-4 border-t border-gray-200">
        <Button
          variant="outline"
          disabled={!identityId}
          title={identityId ? undefined : "No user linked to this transaction"}
          onClick={goToUser}
        >
          User Details
        </Button>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default TransactionDetailsModal;
